import { Metric } from '@repo/types';
import { calculatePreviewChartContext } from '@repo/utils';
import c from 'classnames';
import { Icon, Typography } from 'components';
import PreviewChart from 'components/PreviewChart/PreviewChart';
import { userValueFormat } from 'helpers';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import useGetKitIdParam from 'hooks/useGetKitIdParam';
import { Link, useParams } from 'react-router-dom';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import { EvaluationPillFromColor } from '../../SubMetricGraphs/SubMetricGraphs';
import CategoryTags from './CategoryTags';
import styles from './MetricsCategories.module.css';

type MetricRowProps = {
  metric: Metric;
  idx: number;
  topPriorityRanks: Map<number, number>;
  hasAnyChart: boolean;
};

export default function MetricRow({
  metric,
  idx,
  topPriorityRanks,
  hasAnyChart,
}: MetricRowProps) {
  const kitId = useGetKitIdParam();
  const { data: allActions } = useActionPlanData({ kitId: kitId });

  const { category } = useParams<{ category: string }>();

  return (
    <div className={styles.metricRow}>
      <div className={styles.textsContainer}>
        <Typography variant='body-s'>{metric.name}</Typography>
        {idx === 0 && (
          <div className={c(styles.columns, hasAnyChart && styles.hasChart)}>
            <Typography variant='body-xs' color='grey'>
              Result
            </Typography>
            {hasAnyChart && (
              <Typography
                variant='body-xs'
                color='grey'
                className='hide-on-mobile'
              >
                Progress
              </Typography>
            )}
          </div>
        )}
      </div>
      <div
        className={c(
          styles.submetricsContainer,
          metric.orderedSubmetrics.length === 1 && styles.singleSubmetric,
        )}
      >
        {metric.orderedSubmetrics.map((submetric, idx) => {
          const parsedValue = userValueFormat(
            submetric.user_value,
            submetric.data_type,
            submetric.user_string,
          );
          const unit = submetric?.gut_vaginal_health?.unit;
          const withUnitValue = `${parsedValue}${unit ? ` ${unit}` : ''}`;

          const previewChartContext = calculatePreviewChartContext(submetric);
          const reserveChartSpace =
            hasAnyChart && !previewChartContext.shouldRender;

          const actions = getActionsForSubMetric(
            allActions ?? [],
            submetric.sample_health_id,
          );
          const strains = new Set(
            (submetric.gut_vaginal_health_strain_kits ?? []).map(
              strain => strain.strain_name,
            ),
          ).size;

          return (
            <Link
              key={`${submetric.id}-${idx}`}
              to={`/results/${kitId}/insights/${category ?? 'all-insights'}/${
                submetric.sample_health_id
              }`}
              className={styles.submetric}
            >
              <EvaluationPillFromColor
                evaluation_color={submetric.evaluation_color}
                showIconOnly
              />
              <div className={styles.leftSide}>
                <div className={styles.nameContainer}>
                  <Typography
                    variant='body-s'
                    color='dark'
                    className={styles.name}
                  >
                    {submetric.name}
                  </Typography>
                  <CategoryTags
                    submetric={submetric}
                    topPriorityRanks={topPriorityRanks}
                    className='hide-on-mobile'
                  />
                </div>
                {actions.length > 0 || strains > 0 ? (
                  <div className={styles.additionalInfo}>
                    {actions.length > 0 && (
                      <Typography variant='body-xs' color='grey'>
                        {actions.length} recommended action
                        {actions.length > 1 ? 's' : ''}
                      </Typography>
                    )}
                    {strains > 0 && (
                      <Typography variant='body-xs' color='grey'>
                        {`${strains} strain${strains > 1 ? 's' : ''} detected`}
                      </Typography>
                    )}
                    <CategoryTags
                      submetric={submetric}
                      topPriorityRanks={topPriorityRanks}
                      className='hide-on-desktop'
                    />
                  </div>
                ) : null}
              </div>
              <div className={styles.valueContainer}>
                <Typography
                  variant='body-s'
                  className={c(
                    styles.value,
                    reserveChartSpace && styles.reserveChartSpace,
                  )}
                  color={submetric.evaluation_color}
                >
                  {withUnitValue}
                </Typography>
                {previewChartContext.shouldRender && (
                  <PreviewChart
                    context={previewChartContext}
                    className='hide-on-mobile'
                  />
                )}
                <Icon
                  name='chevronForward'
                  size='s'
                  className={styles.chevron}
                />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
